'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, Film, ArrowLeft, CheckCircle, User, Eye, EyeOff } from 'lucide-react';
import Image from 'next/image';
import { AnimatedContentBackground } from '@/components/AnimatedContentBackground';
import { useSeasonalLogo } from '@/hooks/useSeasonalLogo';
import { getAuthRedirect, authRedirectQuery, signUpErrorMessage } from '@/lib/utils';

export default function SignUpPage() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [redirectQuery, setRedirectQuery] = useState('');
  const { signUp, signInWithGoogle } = useAuth();
  const router = useRouter();
  const { seasonalLogo, themeColor, loading: logoLoading } = useSeasonalLogo();

  // Read ?redirect= after mount (window isn't available during SSR)
  useEffect(() => {
    setRedirectQuery(authRedirectQuery());
  }, []);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signUp(email.trim(), password, username.trim());
      toast.success('Account created! Please verify your email.');
      // Carry the destination through verification
      router.push(`/verify-email${redirectQuery}`);
    } catch (error: any) {
      const suggestions: string[] = error?.suggestions || [];
      toast.error(signUpErrorMessage(error), {
        description: suggestions.length > 0 ? `Available: ${suggestions.slice(0, 3).join(', ')}` : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      toast.success('Signed up with Google!');
      router.push(getAuthRedirect());
    } catch (error: any) {
      // Don't show error if user simply closed the popup
      // ACCOUNT_EXISTS_LINK_REQUIRED already showed its own explanatory toast
      if (error?.code !== 'auth/popup-closed-by-user' && error?.code !== 'auth/cancelled-popup-request' && error?.message !== 'ACCOUNT_EXISTS_LINK_REQUIRED') {
        toast.error('Failed to sign up with Google');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Animated Content Background */}
      <AnimatedContentBackground />

      {/* Content */}
      <div className="relative z-30 flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-md">
          {/* Back to Home Button */}
          <div className="mb-6">
            <Link
              href="/"
              className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Epify
            </Link>
          </div>

          {/* Logo/Brand Section */}
          <div className="text-center mb-8">
            <div className="mb-4 h-[80px] flex items-center justify-center">
              {!logoLoading && (
                <Image
                  src={seasonalLogo?.url || "/image/epify.png"}
                  alt="Epify"
                  width={150}
                  height={150}
                  className="object-contain mx-auto"
                  style={{ maxHeight: '70px', width: 'auto', height: 'auto' }}
                  quality={100}
                  priority
                />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Join Today</h1>
            <p className="text-gray-400 text-sm">
              Start tracking your favorite shows and movies
            </p>
          </div>

          {/* Sign Up Card */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
            <form onSubmit={handleSignUp} className="space-y-6">
              {/* Username Field */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-300 text-sm font-medium">
                  Username
                </Label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400 group-focus-within:text-red-600 transition-colors" />
                  </div>
                  <Input
                    id="username"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    minLength={3}
                    maxLength={20}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                    className="h-12 pl-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-all"
                    placeholder="Choose a username"
                    aria-describedby="username-hint"
                  />
                </div>
                <p id="username-hint" className="text-xs text-gray-500">
                  3–20 characters: letters, numbers, _ or -
                </p>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300 text-sm font-medium">
                  Email Address
                </Label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-red-600 transition-colors" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="h-12 pl-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300 text-sm font-medium">
                  Password
                </Label>
                <div className="relative group theme-input-group">
                  <style>{`
                    .theme-input-group:focus-within .theme-icon {
                      color: ${themeColor} !important;
                    }
                    .theme-input:focus {
                      border-color: ${themeColor}80 !important;
                      box-shadow: 0 0 0 2px ${themeColor}1A !important;
                    }
                    .theme-eye-icon:hover {
                      color: ${themeColor} !important;
                    }
                  `}</style>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 transition-colors theme-icon" />
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="theme-input h-12 pl-10 pr-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl transition-all"
                    placeholder="Minimum 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center theme-eye-icon text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-300 text-sm font-medium">
                  Confirm Password
                </Label>
                <div className="relative group theme-input-group">
                  <style>{`
                    .theme-input-group:focus-within .theme-icon {
                      color: ${themeColor} !important;
                    }
                    .theme-input:focus {
                      border-color: ${themeColor}80 !important;
                      box-shadow: 0 0 0 2px ${themeColor}1A !important;
                    }
                    .theme-eye-icon:hover {
                      color: ${themeColor} !important;
                    }
                  `}</style>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 transition-colors theme-icon" />
                  </div>
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="theme-input h-12 pl-10 pr-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl transition-all"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center theme-eye-icon text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <p className="text-green-200 text-xs font-medium">Password Requirements</p>
                </div>
                <ul className="text-xs text-gray-400 space-y-1">
                  <li className={`flex items-center space-x-2 ${password.length >= 6 ? 'text-green-400' : ''}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${password.length >= 6 ? 'bg-green-500' : 'bg-gray-600'}`} />
                    <span>At least 6 characters</span>
                  </li>
                  <li className={`flex items-center space-x-2 ${password === confirmPassword && password.length > 0 ? 'text-green-400' : ''}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${password === confirmPassword && password.length > 0 ? 'bg-green-500' : 'bg-gray-600'}`} />
                    <span>Passwords match</span>
                  </li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !username || !email || !password || password !== confirmPassword || password.length < 6}
                className="w-full h-12 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-red-500/25"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700/50" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-900/80 px-4 text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Google Sign Up */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-12 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-xl border border-gray-300 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </button>

            {/* Sign In Link */}
            <div className="text-center mt-6 pt-6 border-t border-gray-800/50">
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link
                  href={`/auth/signin${redirectQuery}`}
                  className="text-red-400 hover:text-red-300 font-medium transition-colors"
                >
                  Sign in →
                </Link>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-500">
              By signing up, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}